// components/create/CoverImageModal.js
//
// Choosing a cover, with the same ways in as the app.
//
// ── What this replaces ─────────────────────────────────────────────────────
//
// Three tabs, two of which said "coming soon". Upload worked; Stock Images and
// GIFs were placeholders. So the only cover a web host could choose was a file
// off their own disk — while the app offers a searchable template library, a
// GIF search, an upload and a YouTube video, and the template library is where
// most covers actually come from.
//
// Everything those tabs need already exists server-side and needs no app:
//   * Templates — the `cover_image_templates` table, filtered by `cover_type`
//     so invitations and cards get their own sets, searched through the
//     `tag_synonyms` table exactly as the app searches it.
//   * GIFs — the `klipy-gifs` Edge Function. The KLIPY key stays in Edge
//     Function secrets and never reaches this bundle, which is the whole
//     reason that function exists.
//   * YouTube — no key at all: the id comes out of the URL and the thumbnail
//     off img.youtube.com.
//
//   * PDF — the Cloud Run converter the app already uses. Rasterising a PDF
//     in the browser would mean shipping pdf.js; the service exists, takes a
//     Supabase token and sets CORS, so the browser can call it directly. It
//     runs after the invitation is saved, because it files pages under an
//     event id — see services/pdfCoverService.js.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FiX, FiSearch } from 'react-icons/fi';
import supabase from '../../services/supabase';
import { validateAndExtractYouTubeData } from '../../utils/youtubeUtils';
import { validatePdfFile, formatFileSize, guessPageCount } from '../../utils/pdfCoverUtils';
import '../../styles/CoverImageModal.scss';
import Icon from '../Icon';

/** Chips above the template grid. Mirrors the app's EVENT_TYPES. */
const EVENT_TYPES = [
  'All', 'Birthday Party', 'Wedding', 'Engagement Party', 'Anniversary',
  'Diwali', 'Holi', 'Christmas Party', 'New Year Party', 'Baby Shower',
  'Bridal Shower', 'Mehendi', 'Sangeet', 'Haldi', 'Graduation',
  'Housewarming', 'Office Party', 'Farewell Party', 'Reunion', 'Pooja',
  'Festival', 'Dinner Party', 'Cocktail Party', 'BBQ', 'Beach Party',
  'Pool Party', 'Picnic', 'Game Night', 'Movie Night', 'Karaoke Night',
];

const TABS = [
  { id: 'templates', label: 'Templates', icon: 'image-outline' },
  { id: 'gifs', label: 'GIFs', icon: 'flash' },
  { id: 'upload', label: 'Upload', icon: 'cloud-upload' },
  { id: 'pdf', label: 'PDF', icon: 'document-outline' },
  { id: 'youtube', label: 'YouTube', icon: 'play-circle' },
];

const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

export default function CoverImageModal({ onClose, onSelectImage, eventType, context = 'event' }) {
  const [tab, setTab] = useState('templates');
  const [query, setQuery] = useState('party');
  const [typeFilter, setTypeFilter] = useState(eventType || 'All');

  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  const [gifs, setGifs] = useState([]);
  const [loadingGifs, setLoadingGifs] = useState(false);
  const [gifError, setGifError] = useState(null);

  const [upload, setUpload] = useState(null);
  const [uploadError, setUploadError] = useState(null);

  const [pdf, setPdf] = useState(null);
  const [pdfError, setPdfError] = useState(null);

  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [youtubeBusy, setYoutubeBusy] = useState(false);
  const [youtubeError, setYoutubeError] = useState(null);
  const [youtubePreview, setYoutubePreview] = useState(null);

  // Escape closes, and the page behind does not scroll while this is open.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  // An object URL holds the whole file in memory until it is revoked, and a
  // 50MB PDF is not something to leak because someone changed their mind.
  useEffect(() => () => { if (pdf?.previewUrl) URL.revokeObjectURL(pdf.previewUrl); }, [pdf]);

  // ── Templates ────────────────────────────────────────────────────────────

  const loadTemplates = useCallback(async (type, search) => {
    setLoadingTemplates(true);
    try {
      const term = (search || '').toLowerCase().trim();
      // A search goes through search_cover_templates (the app's migration
      // 20261004100000), as in the app: every typed word matches the START of
      // a word in any tag or the event type, synonyms included - "sun" and
      // "sund" already find "sunday lunch". `legacy` is the exact-tag search
      // it replaced, used only until that migration is applied.
      const build = (legacyTerms) => {
        let q = (term && !legacyTerms
          ? supabase.rpc('search_cover_templates', { p_query: term })
          : supabase.from('cover_image_templates'))
          .select('id, event_type, cover_type, thumbnail_url, webp_url, keywords, width, height')
          .eq('cover_type', context === 'card' ? 'card' : 'invitation')
          .order('created_at', { ascending: false })
          .limit(60);
        if (type && type !== 'All') q = q.eq('event_type', type);
        if (legacyTerms) {
          // Any of the terms (overlaps), quoted so "happy birthday" stays one.
          const clean = (t) => String(t).replace(/["{}(),]/g, ' ').trim();
          const list = legacyTerms.map(clean).filter(Boolean).map((t) => `"${t}"`).join(',');
          q = q.or(`keywords.ov.{${list}},event_type.ilike.%${clean(term)}%`);
        }
        return q;
      };

      let { data, error } = await build(null);
      if (error && term) {
        const { data: synonymRow } = await supabase
          .from('tag_synonyms')
          .select('synonyms')
          .eq('tag', term)
          .maybeSingle();
        ({ data, error } = await build(synonymRow?.synonyms ? [term, ...synonymRow.synonyms] : [term]));
      }
      if (error) throw error;
      setTemplates(data || []);
    } catch (err) {
      console.error('Error loading cover templates:', err);
      setTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  }, [context]);

  // ── GIFs ─────────────────────────────────────────────────────────────────

  const searchGifs = useCallback(async (search) => {
    setLoadingGifs(true);
    setGifError(null);
    try {
      const { data, error } = await supabase.functions.invoke('klipy-gifs', {
        body: {
          // An empty box shows trending rather than an empty grid.
          action: search.trim() ? 'search' : 'trending',
          query: search,
          page: 1,
          perPage: 24,
          locale: 'in',
        },
      });
      if (error) throw error;
      if (data?.error) {
        setGifs([]);
        setGifError(data.error);
        return;
      }
      setGifs(data?.gifs ?? []);
    } catch (err) {
      // A picker that took down the cover sheet because GIFs were unavailable
      // would be a bad trade — there are three other ways to choose here.
      console.warn('GIF search failed:', err?.message);
      setGifs([]);
      setGifError('Could not load GIFs right now.');
    } finally {
      setLoadingGifs(false);
    }
  }, []);

  // One debounced effect drives both grids, so typing in the box searches
  // whichever tab is showing without two timers racing each other.
  const debounceRef = useRef(null);
  useEffect(() => {
    if (tab !== 'templates' && tab !== 'gifs') return undefined;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (tab === 'templates') loadTemplates(typeFilter, query);
      else searchGifs(query);
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [tab, query, typeFilter, loadTemplates, searchGifs]);

  // ── Handlers ─────────────────────────────────────────────────────────────

  const pickTemplate = (template) => {
    onSelectImage({
      // A template is already on the CDN, so it is never re-uploaded — the
      // app marks it the same way and skips its upload step.
      type: 'template',
      uri: template.webp_url || template.thumbnail_url,
      cdnUrl: template.webp_url || template.thumbnail_url,
      templateId: template.id,
      width: template.width,
      height: template.height,
      isAnimated: false,
    });
    onClose();
  };

  const pickGif = (gif) => {
    onSelectImage({
      type: 'gif',
      uri: gif.url,
      downsizedUrl: gif.downsizedUrl ?? gif.url,
      // The still that list rows and link previews use — a feed of animating
      // GIFs is what a poster frame exists to avoid.
      staticUri: gif.preview ?? gif.blurPreview ?? null,
      isAnimated: true,
    });
    onClose();
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadError(null);

    if (!file.type.startsWith('image/')) {
      setUploadError('Pick an image file — JPG, PNG or GIF.');
      return;
    }
    // Checked here rather than at submit: discovering it after filling in the
    // whole form is the worst possible moment.
    if (file.size > MAX_UPLOAD_BYTES) {
      setUploadError('That image is over 10MB. Try a smaller one.');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const isGif = file.type.includes('gif');
      setUpload({
        uri: reader.result,
        type: isGif ? 'gif' : 'image',
        isAnimated: isGif,
      });
    };
    reader.onerror = () => setUploadError('That file could not be read.');
    reader.readAsDataURL(file);
  };

  const onPdf = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setPdfError(null);

    const { valid, error } = validatePdfFile(file);
    if (!valid) { setPdfError(error); return; }

    // The file itself is kept, not its bytes: it is base64-encoded at save,
    // not now. Encoding a 50MB PDF the moment it is picked would block the
    // page for seconds before anyone had decided to use it.
    setPdf({
      file,
      name: file.name,
      size: file.size,
      previewUrl: URL.createObjectURL(file),
      pageCount: await guessPageCount(file),
    });
  };

  const usePdf = () => {
    if (!pdf) return;
    onSelectImage({
      type: 'pdf',
      // Carried through the form and converted after the invitation is
      // saved — the service files pages under an event id, so there is
      // nothing to convert until one exists.
      file: pdf.file,
      fileName: pdf.name,
      fileSize: pdf.size,
      // The blob URL is what the picker and the form preview show until the
      // real page images come back.
      uri: pdf.previewUrl,
      pageCount: pdf.pageCount,
      isAnimated: false,
    });
    onClose();
  };

  const submitYoutube = async () => {
    setYoutubeBusy(true);
    setYoutubeError(null);
    setYoutubePreview(null);
    try {
      const result = await validateAndExtractYouTubeData(youtubeUrl);
      if (!result.ok) {
        setYoutubeError(result.error);
        return;
      }
      setYoutubePreview(result);
    } finally {
      setYoutubeBusy(false);
    }
  };

  const confirmYoutube = () => {
    if (!youtubePreview) return;
    onSelectImage({
      type: 'youtube',
      videoId: youtubePreview.videoId,
      originalUrl: youtubePreview.originalUrl,
      uri: youtubePreview.thumbnailUrl,
      thumbnailUrl: youtubePreview.thumbnailUrl,
      thumbnailQuality: youtubePreview.thumbnailQuality,
      isAnimated: false,
    });
    onClose();
  };

  const searchable = tab === 'templates' || tab === 'gifs';

  return (
    <div className="cover-modal-scrim" onClick={onClose}>
      <div
        className="cover-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Select a cover"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="cover-modal-head">
          <h2>Select cover</h2>
          <button type="button" onClick={onClose} aria-label="Close">
            <FiX />
          </button>
        </div>

        <div className="cover-modal-tabs" role="tablist">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              className={`cover-modal-tab ${tab === t.id ? 'is-active' : ''}`}
              onClick={() => setTab(t.id)}
            >
              <Icon name={t.icon} size={16} />
              {t.label}
            </button>
          ))}
        </div>

        {searchable && (
          <div className="cover-modal-search">
            <FiSearch />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={tab === 'templates' ? 'Search templates…' : 'Search GIFs…'}
              aria-label={tab === 'templates' ? 'Search templates' : 'Search GIFs'}
            />
          </div>
        )}

        {tab === 'templates' && (
          <div className="cover-modal-chips">
            {EVENT_TYPES.map((type) => (
              <button
                key={type}
                type="button"
                className={`cover-modal-chip ${typeFilter === type ? 'is-active' : ''}`}
                onClick={() => setTypeFilter(type)}
              >
                {type}
              </button>
            ))}
          </div>
        )}

        <div className="cover-modal-body">
          {tab === 'templates' && (
            loadingTemplates ? (
              <div className="cover-modal-grid">
                {[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="cover-modal-skeleton" />)}
              </div>
            ) : templates.length === 0 ? (
              <p className="cover-modal-note">
                Nothing matches “{query}”. Try another word, or a different event type.
              </p>
            ) : (
              <div className="cover-modal-grid">
                {templates.map((template) => (
                  <button
                    key={template.id}
                    type="button"
                    className="cover-modal-tile"
                    onClick={() => pickTemplate(template)}
                  >
                    <img src={template.thumbnail_url || template.webp_url} alt="" loading="lazy" />
                  </button>
                ))}
              </div>
            )
          )}

          {tab === 'gifs' && (
            loadingGifs ? (
              <div className="cover-modal-grid">
                {[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="cover-modal-skeleton" />)}
              </div>
            ) : gifError ? (
              <p className="cover-modal-note">{gifError}</p>
            ) : gifs.length === 0 ? (
              <p className="cover-modal-note">No GIFs for “{query}”.</p>
            ) : (
              <>
                <div className="cover-modal-grid">
                  {gifs.map((gif) => (
                    <button
                      key={gif.id}
                      type="button"
                      className="cover-modal-tile"
                      onClick={() => pickGif(gif)}
                    >
                      <img src={gif.preview || gif.downsizedUrl || gif.url} alt="" loading="lazy" />
                    </button>
                  ))}
                </div>
                {/* KLIPY's terms require the attribution wherever their
                    results are shown. */}
                <p className="cover-modal-attribution">Powered by KLIPY</p>
              </>
            )
          )}

          {tab === 'upload' && (
            <div className="cover-modal-upload">
              <input
                type="file"
                accept="image/*"
                onChange={onFile}
                id="cover-file"
                className="cover-modal-file"
              />
              <label htmlFor="cover-file" className="cover-modal-drop">
                {upload ? (
                  <img src={upload.uri} alt="" />
                ) : (
                  <>
                    <Icon name="cloud-upload" size={30} />
                    <strong>Choose an image</strong>
                    <small>JPG, PNG or GIF, up to 10MB</small>
                  </>
                )}
              </label>

              {uploadError && <p className="cover-modal-error">{uploadError}</p>}

              {upload && (
                <button
                  type="button"
                  className="cover-modal-primary"
                  onClick={() => { onSelectImage(upload); onClose(); }}
                >
                  Use this image
                </button>
              )}
            </div>
          )}

          {tab === 'pdf' && (
            <div className="cover-modal-upload">
              <input
                type="file"
                accept="application/pdf,.pdf"
                onChange={onPdf}
                id="cover-pdf"
                className="cover-modal-file"
              />
              <label htmlFor="cover-pdf" className="cover-modal-drop">
                {pdf ? (
                  <>
                    {/* The browser's own PDF viewer. Desktop Chrome, Firefox
                        and Safari render the first page inline; a phone
                        usually will not, and gets the card below instead.
                        Shipping pdf.js to cover that gap would cost several
                        megabytes on a bundle already too big, for a preview
                        that is replaced by real page images at save. */}
                    <object data={pdf.previewUrl} type="application/pdf" className="cover-modal-pdf">
                      <span className="cover-modal-pdf-fallback">
                        <Icon name="document-outline" size={30} />
                        <strong>{pdf.name}</strong>
                        <small>
                          {formatFileSize(pdf.size)}
                          {pdf.pageCount ? ` · ${pdf.pageCount} pages` : ''}
                        </small>
                      </span>
                    </object>
                  </>
                ) : (
                  <>
                    <Icon name="document-outline" size={30} />
                    <strong>Choose a PDF</strong>
                    <small>Up to 50MB. Every page becomes part of the invitation.</small>
                  </>
                )}
              </label>

              {pdf && (
                <p className="cover-modal-note">
                  <strong>{pdf.name}</strong> · {formatFileSize(pdf.size)}
                  {pdf.pageCount ? ` · ${pdf.pageCount} pages` : ''}
                </p>
              )}

              {pdfError && <p className="cover-modal-error">{pdfError}</p>}

              {pdf && (
                <button type="button" className="cover-modal-primary" onClick={usePdf}>
                  Use this PDF
                </button>
              )}

              <p className="cover-modal-note">
                The pages are turned into images when you publish, so this takes a
                little longer to save than a photo does. Guests swipe through them
                on the invitation.
              </p>
            </div>
          )}

          {tab === 'youtube' && (
            <div className="cover-modal-youtube">
              <label htmlFor="cover-yt">Paste a YouTube video link</label>
              <input
                id="cover-yt"
                type="url"
                inputMode="url"
                value={youtubeUrl}
                onChange={(e) => { setYoutubeUrl(e.target.value); setYoutubeError(null); }}
                placeholder="https://www.youtube.com/watch?v=…"
              />

              <button
                type="button"
                className="cover-modal-primary"
                onClick={submitYoutube}
                disabled={!youtubeUrl.trim() || youtubeBusy}
              >
                {youtubeBusy ? 'Checking…' : 'Check link'}
              </button>

              {youtubeError && <p className="cover-modal-error">{youtubeError}</p>}

              {youtubePreview && (
                <div className="cover-modal-yt-preview">
                  <img src={youtubePreview.thumbnailUrl} alt="" />
                  <button type="button" className="cover-modal-primary" onClick={confirmYoutube}>
                    Use this video
                  </button>
                </div>
              )}

              <p className="cover-modal-note">
                The video plays on the invitation. Guests cannot watch a private or
                unlisted video, so use one that is public.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
